import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Eye, Users } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { key: "today", label: "Bugün" },
  { key: "7", label: "Son 7 Gün" },
  { key: "30", label: "Son 30 Gün" },
  { key: "90", label: "Son 90 Gün" },
];

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-xl bg-[#FBEEF0] flex items-center justify-center"><Icon className="w-5 h-5 text-[#9E2A3B]" /></div>
    <div>
      <p className="text-sm text-[#7C6E70]">{label}</p>
      <p className="font-display text-3xl text-[#2A2224]" data-testid={testid}>{value.toLocaleString("tr-TR")}</p>
    </div>
  </div>
);

const TopList = ({ title, items, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.views));
  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid={testid}>
      <p className="text-sm font-medium text-[#2A2224] mb-4">{title}</p>
      {!items.length ? (
        <p className="text-sm text-[#a8999b] py-6 text-center">Bu dönemde veri yok.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((it, i) => (
            <li key={it.slug || it.name} data-testid={`${testid}-row-${i}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#2A2224]"><span className="text-[#a8999b] mr-2">{i + 1}.</span>{it.name}</span>
                <span className="font-medium text-[#9E2A3B]">{it.views.toLocaleString("tr-TR")}</span>
              </div>
              <div className="h-2 rounded-full bg-[#FBEEF0] overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] transition-[width] duration-500" style={{ width: `${(it.views / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const SiteVisits = ({ headers }) => {
  const [range, setRange] = useState("7");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/site-visits`, { headers, params: { range } }).then((r) => setData(r.data)).catch(() => setData({ series: [], totalViews: 0, totalVisitors: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  return (
    <div className="space-y-6" data-testid="site-visits-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl text-[#2A2224]">Site Ziyaretleri</h2>
          <p className="text-sm text-[#7C6E70] mt-1">Ziyaretçilerin gördüğü sayfalar sayılır; patron ve firma panelleri hariçtir.</p>
        </div>
        <div className="flex flex-wrap gap-1 bg-white border border-[#F0DDE0] rounded-xl p-1">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`visits-range-${r.key}`}
              className={`px-3.5 py-2 rounded-lg text-sm transition-colors ${range === r.key ? "bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white" : "text-[#5b4d4f] hover:bg-[#FBF5F0]"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 text-[#9E2A3B] animate-spin" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <Stat icon={Users} label="Tekil Ziyaretçi" value={data.totalVisitors} testid="visits-total-visitors" />
            <Stat icon={Eye} label="Sayfa Görüntüleme" value={data.totalViews} testid="visits-total-views" />
          </div>
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="visits-chart">
            <p className="text-sm font-medium text-[#2A2224] mb-4">{range === "today" ? "Saatlik" : "Günlük"} ziyaretçi ve sayfa görüntüleme</p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ left: -15, right: 10 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C15B6B" stopOpacity={0.35} /><stop offset="100%" stopColor="#C15B6B" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C6A15B" stopOpacity={0.35} /><stop offset="100%" stopColor="#C6A15B" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3E6E8" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7C6E70" }} interval="preserveStartEnd" minTickGap={16} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#7C6E70" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0DDE0" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="views" name="Sayfa Görüntüleme" stroke="#C15B6B" strokeWidth={2} fill="url(#gViews)" />
                  <Area type="monotone" dataKey="visitors" name="Tekil Ziyaretçi" stroke="#C6A15B" strokeWidth={2} fill="url(#gVis)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <TopList title="En Çok Bakılan Kategoriler" items={data.topCategories || []} testid="visits-top-categories" />
            <TopList title="En Çok Bakılan Şehirler" items={data.topCities || []} testid="visits-top-cities" />
          </div>
        </>
      )}
    </div>
  );
};
